import { useState, useEffect } from 'react';

interface Release {
  id: number;
  title: string;
  date: string;
  description: string;
  daysRemaining: number;
  cover: string;
}

export default function UpcomingReleases() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const releases: Release[] = [
    {
      id: 1,
      title: 'M.A.I.D for You',
      date: 'Spring 2026',
      description: 'The dark truth of the assisted suicide program in Canada',
      daysRemaining: 28,
      cover: 'https://static.readdy.ai/image/4290f71dfbaa025f6a9f1c6e0e6bc4ce/7f3c04d9dd918841a98ccc811cb79f03.jpeg'
    },
    {
      id: 2,
      title: 'Raining Money',
      date: 'Summer 2026',
      description: 'A look into where Canada spends tax dollars.',
      daysRemaining: 65,
      cover: 'https://static.readdy.ai/image/4290f71dfbaa025f6a9f1c6e0e6bc4ce/c05b7879b3e5647ea5bc6521b86846c1.jpeg'
    },
    {
      id: 3,
      title: 'Crossing the Floor',
      date: 'Summer 2026',
      description: 'A song about the dangers of bribes, traitors and foreign interference.',
      daysRemaining: 104,
      cover: 'https://static.readdy.ai/image/4290f71dfbaa025f6a9f1c6e0e6bc4ce/64c5e07941ed3562eb416b5a194424cc.jpeg'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6omjauancq2eie820q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <section id="upcoming-releases" className="relative py-32 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]">
      {/* Diagonal Stripe Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)',
        backgroundSize: '10px 10px'
      }}></div>

      <div className="relative max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-4xl font-black tracking-wider text-white uppercase glow-text-blue">
            COMING SOON
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#333333] transform -translate-x-1/2"></div>

          {/* Release Items */}
          <div className="space-y-16">
            {releases.map((release, index) => (
              <div
                key={release.id}
                className={`relative flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Connector Line */}
                <div className="hidden md:block absolute left-1/2 top-1/2 w-16 h-0.5 bg-[#333333] transform -translate-y-1/2" style={{
                  [index % 2 === 0 ? 'right' : 'left']: '50%',
                  [index % 2 === 0 ? 'marginRight' : 'marginLeft']: '-60px'
                }}></div>

                {/* Timeline Dot */}
                <div className="hidden md:block absolute left-1/2 top-1/2 w-4 h-4 bg-[#ff0033] rounded-full transform -translate-x-1/2 -translate-y-1/2 pulse-dot"></div>

                {/* Release Card */}
                <div className={`w-full md:w-[480px] bg-[#121212] border border-[#2a2a2a] rounded-2xl overflow-hidden hover:scale-102 transition-all duration-300 ${
                  index % 2 === 0 ? 'md:ml-auto md:mr-8' : 'md:mr-auto md:ml-8'
                }`}
                style={{
                  boxShadow: '0 0 0 rgba(77,208,225,0)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(77,208,225,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 rgba(77,208,225,0)';
                }}>
                  <div className="flex flex-col sm:flex-row">
                    {/* Cover Image */}
                    <div className="w-full sm:w-36 h-36 flex-shrink-0">
                      <img
                        src={release.cover}
                        alt={release.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-6">
                      <h3 className="text-2xl font-bold text-white mb-2">{release.title}</h3>
                      <p className="text-base text-[#ff0033] font-semibold mb-3">{release.date}</p>
                      <p className="text-sm text-gray-400 leading-relaxed mb-4">{release.description}</p>
                      {index === 0 && (
                        <a
                          href="https://youtu.be/ph_0oFvlUSU"
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff0000] hover:bg-[#cc0000] text-white text-xs font-bold tracking-wider rounded-full transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,0,0,0.5)] whitespace-nowrap cursor-pointer"
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-youtube-fill text-base"></i>
                          </span>
                          WATCH ON YOUTUBE
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Email Signup */}
        <div className="mt-24 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Get Early Access to New Nightmares</h3>
          <p className="text-gray-400 mb-8">Be the first to know when new music drops</p>
          
          <form 
            id="mailing-list-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              maxLength={100}
              className="w-full sm:w-96 px-6 py-4 bg-[#0f0f0f] border-2 border-[#ff0033] rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-[#ff0033] focus:shadow-[0_0_20px_rgba(255,0,51,0.3)] transition-all"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-10 py-4 bg-[#ff0033] text-white font-bold text-sm tracking-wider rounded-full hover:bg-[#cc0029] hover:shadow-[0_0_30px_rgba(255,0,51,0.6)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'SENDING...' : 'NOTIFY ME'}
            </button>
          </form>

          {submitStatus === 'success' && (
            <p className="mt-4 text-[#4dd0e1] text-sm font-semibold">Successfully subscribed! Check your inbox.</p>
          )}
          {submitStatus === 'error' && (
            <p className="mt-4 text-[#ff0033] text-sm font-semibold">Something went wrong. Please try again.</p>
          )}
        </div>
      </div>
    </section>
  );
}